/**
 * StatusBadge.js — shared status-pill component driven by ThemeContext's
 * T.statusPill(tone) (see utils/themeStyles.js), which follows the admin's
 * chosen Button & Status Style (Config Master → Branding).
 *
 * `tone` is a semantic bucket, not a raw color — callers map their own
 * status strings (e.g. "confirmed", "pending_payment", "cancelled") to one
 * of these 5 tones themselves, since the set of real statuses differs per
 * screen; this component only owns how a tone is rendered, not what each
 * status means.
 *
 *   tone: "success" | "warning" | "danger" | "info" | "neutral"
 *
 * Renders as a small solid dot + label. The flat/tinted family (solid/soft/
 * outline) never has a border or shadow — that's what keeps it from reading
 * as a button (see ThemedButton.js, which owns the "raised, shadowed,
 * clickable" look exclusively). The material-driven styles (neumorphism,
 * glassmorphism, polymorphism, vintage, gradient, nature) instead express
 * "not a button" through their own passive material — a recessed inset
 * shadow, a thin glass border, a bordered geometric tag — read generically
 * from whatever `buildStatusPill` returns for the active style, never a
 * raised/shadowed look. `dotRadius` varies the mark's shape (circle/square/
 * organic) to match each style's own geometry. `icon` is still accepted for
 * a screen that wants one in addition to the dot.
 */
import React from "react";
import { useTheme, getT } from "../ThemeContext";

export default function StatusBadge({ label, tone = "neutral", icon, style }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const s = T.statusPill(tone);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "3px 10px", borderRadius: s.borderRadius ?? 20, fontSize: 11, fontWeight: 700,
      whiteSpace: "nowrap", fontFamily: "inherit",
      background: s.background, color: s.color,
      border: s.border, boxShadow: s.boxShadow, backdropFilter: s.backdropFilter, WebkitBackdropFilter: s.backdropFilter,
      ...style,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: s.dotRadius || "50%", background: s.dotColor, flexShrink: 0 }} />
      {icon}{label}
    </span>
  );
}
